<template>
  <section
    id="products"
    ref="revealTarget"
    class="section products-section home-scroll-reveal"
    :class="{ 'is-reveal-ready': isRevealReady, 'is-revealed': isRevealed }"
  >
    <div class="page-container">
      <div class="section-heading-row">
        <div class="section-head">
          <span class="section-kicker">FEATURED PRODUCTS</span>
          <h2>从热带作物到特色产品</h2>
        </div>
        <nuxt-link class="section-more" to="/mall">
          查看更多
          <i class="i-custom-enter more-link-icon-desktop" aria-hidden="true"></i>
          <i class="i-lucide-chevron-right more-link-icon-mobile" aria-hidden="true"></i>
        </nuxt-link>
      </div>

      <div v-if="products.length === 0" class="products-status">暂时没有产品</div>
      <div v-else class="product-track">
        <article v-for="product in products" :key="product.id" class="product-card">
          <nuxt-link class="product-image" :to="productPath(product.id)">
            <home-media
              :source="productImage(product)"
              fallback-source="/images/company/strength/capability-industry.webp"
              :alt="product.name"
              fit="contain"
              loading="lazy"
            />
          </nuxt-link>
          <div class="product-info">
            <span class="product-meta">
              {{ product.category.name }}
              <template v-if="product.spec">/ {{ product.spec }}</template>
            </span>
            <h3>
              <nuxt-link :to="productPath(product.id)">{{ product.name }}</nuxt-link>
            </h3>
            <contact-dialog default-type="采购咨询" target="product" :target-id="product.id">
              <button class="product-consult" type="button">
                采购咨询
                <i class="i-custom-enter" aria-hidden="true"></i>
              </button>
            </contact-dialog>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { HomepageProductItem } from '~/types/site-api'

  defineOptions({
    name: 'HomeProducts',
  })

  const props = withDefaults(
    defineProps<{
      items?: HomepageProductItem[]
    }>(),
    {
      items: () => [],
    }
  )

  const {
    targetRef: revealTarget,
    isReady: isRevealReady,
    isVisible: isRevealed,
  } = useScrollReveal(0.12)

  const products = computed(() => props.items.slice(0, 4))

  function productImage(product: HomepageProductItem) {
    return product.cover_url || '/images/company/strength/capability-industry.webp'
  }

  function productPath(id: string) {
    return `/mall/${encodeURIComponent(id)}`
  }
</script>

<style scoped lang="scss">
  .products-section {
    background: var(--surface);
  }

  .section-heading-row {
    display: flex;
    gap: 24px;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 48px;
  }

  .section-head {
    max-width: 780px;
    margin-bottom: 0;
  }

  .section-head h2 {
    margin: 0;
    font-size: clamp(28px, 3.2vw, 44px);
    font-weight: 620;
    line-height: 1.18;
  }

  .section-kicker {
    display: inline-flex;
    gap: 10px;
    align-items: center;
    margin-bottom: 12px;
    font:
      650 12px/1.4 ui-monospace,
      SFMono-Regular,
      Menlo,
      monospace;
    color: var(--accent);
    letter-spacing: 0.1em;
  }

  .section-kicker::before {
    width: 28px;
    height: 2px;
    content: '';
    background: currentColor;
  }

  .section-more {
    display: inline-flex;
    flex: 0 0 auto;
    gap: 8px;
    align-items: center;
    min-height: 44px;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: var(--more);
    white-space: nowrap;
    cursor: pointer;
  }

  .section-more i,
  .product-consult i {
    width: 16px;
    height: 16px;
    transition: transform var(--fast) var(--ease);
  }

  .section-more:hover i,
  .product-consult:hover i {
    transform: translateX(4px);
  }

  .section-more:hover {
    color: var(--brand-strong);
  }

  .products-status {
    display: grid;
    place-items: center;
    min-height: 220px;
    padding: 24px;
    color: var(--muted);
    text-align: center;
    background: var(--surface-soft);
    border-radius: var(--radius-md);
  }

  .product-track {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 22px;
    min-width: 0;
  }

  .product-card {
    min-width: 0;
    overflow: hidden;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
  }

  .product-image {
    display: block;
    aspect-ratio: 1.15 / 1;
    overflow: hidden;
    background: var(--surface-soft);
  }

  .product-image :deep(.home-media) {
    transition: transform var(--normal) var(--ease);
  }

  .product-image:hover :deep(.home-media) {
    transform: scale(1.035);
  }

  .product-info {
    min-height: 164px;
    padding: 26px 24px 24px;
    background: var(--surface);
  }

  .product-meta {
    display: block;
    font-size: 13px;
    line-height: 1.5;
    color: var(--muted);
  }

  .product-info h3 {
    margin: 10px 0 0;
    font-size: 18px;
    font-weight: 620;
    line-height: 1.35;
  }

  .product-info h3 a:hover {
    color: var(--accent);
  }

  .product-consult {
    display: inline-flex;
    gap: 7px;
    align-items: center;
    padding: 0;
    margin-top: 24px;
    font-size: 14px;
    font-weight: 620;
    color: var(--accent);
    cursor: pointer;
    background: none;
    border: 0;
  }

  @media (max-width: 767px) {
    .section-heading-row {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 14px 16px;
      align-items: center;
      margin-bottom: 36px;
    }

    .section-head {
      display: contents;
    }

    .section-kicker {
      grid-row: 1;
      grid-column: 1;
      min-width: 0;
      margin-bottom: 0;
    }

    .section-head h2 {
      grid-row: 2;
      grid-column: 1 / -1;
    }

    .section-more {
      grid-row: 1;
      grid-column: 2;
      align-self: center;
      margin: 0;
    }

    .product-track {
      display: flex;
      gap: 12px;
      padding-bottom: 12px;
      margin: 0 -24px;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scroll-padding-inline: 36px;
      scrollbar-width: none;
    }

    .product-track::-webkit-scrollbar {
      display: none;
    }

    .product-card {
      flex: 0 0 min(78vw, 300px);
      scroll-snap-align: start;
    }

    .product-info {
      min-height: 156px;
      padding: 22px 20px;
    }

    .product-card:first-child {
      margin-left: 36px;
    }

    .product-card:last-child {
      margin-right: 36px;
    }
  }
</style>
